<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import {
		Modal,
		Container,
		InputV2,
		Button,
		ButtonV2,
		Text,
		CircleAvatar,
	} from '@Shelly/shelly-design-system';
	import type { HomeAvatar as HomeAvatarType, HomePlan } from 'src/types/Home';
	import HomeAvatar from '../Homes/HomeAvatar.svelte';
	import AvatarPicker from './AvatarPicker.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { buildPendingHome, isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

	export let showModal = false;
	export let type: 'home' | 'account' = 'home';
	export let title = '';
	export let nameLabel = '';
	export let nameInputId = 'edit-name-avatar-input';
	export let name = '';
	export let avatar: HomeAvatarType = {};
	export let isPremium = false;
	export let loading = false;
	export let allowUpload = true;
	export let allowAvatarEdit = true;

	const dispatch = createEventDispatcher<{
		save: { name: string; avatar: HomeAvatarType };
		close: void;
	}>();

	$: trimmedName = name.trim();
	$: disableSave = !trimmedName || loading;
	$: previewText = avatar?.text || trimmedName.charAt(0) || name.charAt(0);
	$: homePlan = (isPremium ? 'premium' : 'standard') as HomePlan;
	$: previewHome =
		type === 'home' ? buildPendingHome(trimmedName || name, avatar, homePlan) : undefined;

	const closeModal = (): void => {
		showModal = false;
		dispatch('close');
	};

	const onSave = (): void => {
		if (disableSave) return;
		dispatch('save', { name: trimmedName, avatar });
	};
</script>

<Modal {showModal} on:close={closeModal} {title} removeScrollPadding>
	<div slot="content">
		<form autocomplete="off" novalidate class="width-full" on:submit|preventDefault>
			<Container noScroll padding="scrollPaddings">
				<div class="d-flex justify-content-center mb-l">
					{#if type === 'account'}
						<CircleAvatar
							size="xl"
							image={avatar?.image}
							backgroundColor={avatar?.backgroundColor}
							text={previewText}
							avatarOptions={{ type: isPremium ? 'premium' : null }}
						/>
					{:else if previewHome}
						<HomeAvatar home={previewHome} size="xl" />
					{/if}
				</div>
				<Text styleType="h2" class="mb-s">{nameLabel}</Text>
				<InputV2
					id={nameInputId}
					aria-label={nameLabel}
					autocomplete="off"
					bind:value={name}
					error={!trimmedName
						? $format('input_error_empty_name', { default: 'The name can not be empty!' })
						: ''}
					disabled={isActiveHomeShared()}
				/>
			</Container>
			{#if allowAvatarEdit}
				<Container padding="scrollPaddings">
					<hr class="mt-l mb-l" />
					<Text styleType="h2" class="mb-m">
						{$format('label_select_avatar', { default: 'Select an avatar' })}
					</Text>
					<AvatarPicker
						bind:avatar
						{allowUpload}
						uploadShape={type === 'home' ? 'rectangular' : 'circle'}
					/>
				</Container>
			{/if}
		</form>
	</div>
	<div class="d-flex justify-content-end" slot="footer">
		{#if $showNewLayout}
			<ButtonV2
				variant="secondary"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				size="m"
				aria-label={$format('button_cancel', { default: 'Cancel' })}
				on:click={closeModal}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				style="color: var(--color-base) !important;"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				size="m"
				disabled={disableSave}
				{loading}
				aria-label={$format('button_save', { default: 'Save' })}
				on:click={onSave}
			>
				{$format('button_save', { default: 'Save' })}
			</ButtonV2>
		{:else}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				aria-label={$format('button_cancel', { default: 'Cancel' })}
				on:click={closeModal}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="primary"
				disabled={disableSave}
				{loading}
				aria-label={$format('button_save', { default: 'Save' })}
				on:click={onSave}
			>
				{$format('button_save', { default: 'Save' })}
			</Button>
		{/if}
	</div>
</Modal>
